import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

import { ANNOUNCEMENTS_GUIDE_ID } from "@app/util/contexts/GuidesContext";
import useGuides from "@app/util/contexts/useGuides";

const useAnnouncementsSlot = (open: boolean, signedIn: boolean) => {
  const { hideGuide, showGuide } = useGuides();
  // True while the gate holds the slot for a modal that is not open yet.
  const isHoldingRef = useRef(false);
  // True while the open modal owns the slot.
  const isOpenRef = useRef(false);

  const acquire = useCallback(() => {
    if (isOpenRef.current) {
      return false;
    }
    if (!isHoldingRef.current) {
      isHoldingRef.current = showGuide(ANNOUNCEMENTS_GUIDE_ID, { mode: "auto" });
    }
    return isHoldingRef.current;
  }, [showGuide]);

  const release = useCallback(() => {
    if (isOpenRef.current) {
      return;
    }
    if (isHoldingRef.current) {
      isHoldingRef.current = false;
      hideGuide(ANNOUNCEMENTS_GUIDE_ID);
    }
  }, [hideGuide]);

  // The open modal owns the slot until it closes, also after a remount with the modal open.
  // A layout effect, so the gate's layout-phase decisions already see this ownership.
  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    isOpenRef.current = true;
    isHoldingRef.current = false;
    showGuide(ANNOUNCEMENTS_GUIDE_ID, { mode: "force" });
    return () => {
      isOpenRef.current = false;
      isHoldingRef.current = false;
      hideGuide(ANNOUNCEMENTS_GUIDE_ID);
    };
  }, [hideGuide, open, showGuide]);

  useEffect(() => {
    if (!signedIn) {
      release();
    }
  }, [release, signedIn]);

  // A hold never outlives the gate.
  useEffect(() => release, [release]);

  return { acquire, release };
};

export default useAnnouncementsSlot;
